import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { canCreate } from '../../util/access';
import {
	Autocomplete,
	Box,
	Card,
	CardActionArea,
	CardContent,
	Chip,
	CircularProgress,
	Fab,
	Grid,
	InputAdornment,
	Pagination,
	Paper,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import IconButton from "@mui/material/IconButton";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { createFilterOptions, Util } from "../../util/Util";
import CachedItemImage from "../../components/CachedItemImage";
import ItemDetailModal, { CatalogItemRow } from "./ItemDetailModal";
import HubBackButton from "../hubs/HubBackButton";
import { useSessionState } from "../../hooks/useSessionState";

const PAGE_SIZE = 24;

const uniqueSorted = (values: string[]) =>
	Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
		a.localeCompare(b, undefined, { sensitivity: "base" }),
	);

const ProductCatalog = () => {
	const { api, access } = useAuth();
	const { t, queueReRun } = useUI();

	const [items, setItems] = useState<CatalogItemRow[]>([]);
	const [loading, setLoading] = useState(false);
	// Filters kept for the session (the page unmounts on tab switch).
	const [query, setQuery] = useSessionState("catalog:query", "");
	const [categories, setCategories] = useSessionState<string[]>("catalog:categories", []);
	const [families, setFamilies] = useSessionState<string[]>("catalog:families", []);
	const [sectors, setSectors] = useSessionState<string[]>("catalog:sectors", []);
	const [page, setPage] = useSessionState("catalog:page", 1);
	const pageResetReadyRef = useRef(false);
	const [selectedItem, setSelectedItem] = useState<CatalogItemRow | null>(null);
	const [detailOpen, setDetailOpen] = useState(false);
	const [createMode, setCreateMode] = useState(false);
	/** Remount tile main image after upload (IDB cache already updated by modal). */
	const [imageBust, setImageBust] = useState<Record<number, number>>({});
	const fetchGenRef = useRef(0);

	const fetchCatalog = useCallback(async () => {
		const gen = ++fetchGenRef.current;
		setLoading(true);
		try {
			const res = await api.get("items/catalog");
			if (gen !== fetchGenRef.current) return;
			if (res) {
				if (res.status === 200) {
					setItems(res.data.items || []);
				} else if (res.status === 401 || res.status === 422) {
					queueReRun(fetchCatalog);
				}
			}
		} finally {
			if (gen === fetchGenRef.current) {
				setLoading(false);
			}
		}
	}, [api, queueReRun]);

	useEffect(() => {
		void fetchCatalog();
	}, [fetchCatalog]);

	const categoryOptions = useMemo(
		() => uniqueSorted(items.map((i) => i.Category || "")),
		[items],
	);
	const familyOptions = useMemo(
		() => uniqueSorted(items.map((i) => i.Family || "")),
		[items],
	);
	const sectorOptions = useMemo(
		() => uniqueSorted(items.map((i) => i.Sector || "")),
		[items],
	);

	const filtered = useMemo(() => {
		const search = query.trim();
		let result = items;

		if (search) {
			const filter = createFilterOptions(
				(opt: CatalogItemRow) =>
					`${opt.ItemName || ""} ${opt.ItemCode || ""} ${opt.Category || ""} ${opt.Family || ""} ${opt.Sector || ""}`,
			);
			result = filter(items, { inputValue: search });
		} else {
			if (categories.length > 0) {
				result = result.filter((i) => categories.includes(i.Category || ""));
			}
			if (families.length > 0) {
				result = result.filter((i) => families.includes(i.Family || ""));
			}
			if (sectors.length > 0) {
				result = result.filter((i) => sectors.includes(i.Sector || ""));
			}
		}
		return result;
	}, [items, query, categories, families, sectors]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
	const pageSafe = Math.min(page, totalPages);
	const pageItems = useMemo(() => {
		const start = (pageSafe - 1) * PAGE_SIZE;
		return filtered.slice(start, start + PAGE_SIZE);
	}, [filtered, pageSafe]);

	useEffect(() => {
		// Not on mount: a restored page belongs to the restored filters.
		if (!pageResetReadyRef.current) {
			pageResetReadyRef.current = true;
			return;
		}
		setPage(1);
	}, [query, categories, families, sectors]);

	const openDetail = (item: CatalogItemRow) => {
		setCreateMode(false);
		setSelectedItem(item);
		setDetailOpen(true);
	};

	const openCreate = () => {
		setSelectedItem(null);
		setCreateMode(true);
		setDetailOpen(true);
	};

	const closeDetail = () => {
		setDetailOpen(false);
		setSelectedItem(null);
		setCreateMode(false);
	};

	const multiFilterSx = { "& .MuiAutocomplete-tag": { maxWidth: 120 } } as const;

	return (
		<Box
			sx={{
				position: "relative",
				height: "100%",
				maxHeight: "100%",
				display: "flex",
				flexDirection: "column",
				minHeight: 0,
				overflow: "hidden",
				boxSizing: "border-box",
				p: { xs: 1, sm: 1.5 },
				gap: 1,
			}}
		>
			<Paper elevation={2} sx={{ p: { xs: 1, sm: 1.5 }, flexShrink: 0 }}>
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
					<Box>
						<HubBackButton />
						<Typography variant="h6" fontWeight={800}>
							{t("common:productCatalog.title")}
						</Typography>
					</Box>
					<IconButton
						onClick={() => void fetchCatalog()}
						disabled={loading}
						aria-label={t("common:ui.tooltips.refresh")}
					>
						{loading ? <CircularProgress size={22} /> : <RefreshIcon />}
					</IconButton>
				</Box>

				<Grid container spacing={1} sx={{ mb: 1 }}>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Autocomplete
							multiple
							size="small"
							options={categoryOptions}
							value={categories}
							onChange={(_, v) => setCategories(v)}
							disableCloseOnSelect
							disabled={loading}
							filterOptions={createFilterOptions((o: string) => o)}
							renderInput={(params) => (
								<TextField {...params} variant="filled" label={t("common:tables.category")} />
							)}
							sx={multiFilterSx}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Autocomplete
							multiple
							size="small"
							options={familyOptions}
							value={families}
							onChange={(_, v) => setFamilies(v)}
							disableCloseOnSelect
							disabled={loading}
							filterOptions={createFilterOptions((o: string) => o)}
							renderInput={(params) => (
								<TextField {...params} variant="filled" label={t("common:tables.family")} />
							)}
							sx={multiFilterSx}
						/>
					</Grid>
					<Grid size={{ xs: 12, sm: 4 }}>
						<Autocomplete
							multiple
							size="small"
							options={sectorOptions}
							value={sectors}
							onChange={(_, v) => setSectors(v)}
							disableCloseOnSelect
							disabled={loading}
							filterOptions={createFilterOptions((o: string) => o)}
							renderInput={(params) => (
								<TextField {...params} variant="filled" label={t("common:tables.sector")} />
							)}
							sx={multiFilterSx}
						/>
					</Grid>
				</Grid>

				<TextField
					size="small"
					fullWidth
					variant="filled"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					label={t("common:productCatalog.search")}
					disabled={loading}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon fontSize="small" />
							</InputAdornment>
						),
						endAdornment: query ? (
							<InputAdornment position="end">
								<IconButton size="small" onClick={() => setQuery("")} edge="end">
									<ClearIcon fontSize="small" />
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
				/>
			</Paper>

			{/* Tiles: CachedItemImage only (IDB + same resolve path as POS; worker keeps hashes fresh) */}
			<Box
				sx={{
					flex: "1 1 auto",
					minHeight: 0,
					overflow: "auto",
					pb: 1,
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill, minmax(168px, 1fr))",
						gap: 1.5,
						alignContent: "start",
						p: 0.5,
					}}
				>
					{loading && items.length === 0 ? (
						<Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "center", py: 8 }}>
							<CircularProgress />
						</Box>
					) : pageItems.length === 0 ? (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ gridColumn: "1 / -1", textAlign: "center", py: 6 }}
						>
							{t("common:productCatalog.empty")}
						</Typography>
					) : (
						pageItems.map((item) => (
							<Card
								key={item.id}
								elevation={2}
								sx={{
									borderRadius: 2,
									overflow: "hidden",
									display: "flex",
									flexDirection: "column",
									height: "100%",
									opacity: item.IsActive === false ? 0.65 : 1,
									"&:hover": { boxShadow: 6 },
								}}
							>
								<CardActionArea
									onClick={() => openDetail(item)}
									sx={{
										flex: 1,
										display: "flex",
										flexDirection: "column",
										alignItems: "stretch",
										justifyContent: "flex-start",
										color: "text.primary",
										height: "100%",
									}}
								>
									<Box
										sx={{
											position: "relative",
											width: "100%",
											aspectRatio: "1 / 1",
											maxHeight: 180,
											bgcolor: "action.hover",
											flexShrink: 0,
										}}
									>
										<CachedItemImage
											key={`${item.id}-${imageBust[item.id] || 0}`}
											itemId={item.id}
											alt={item.ItemName || ""}
											lazy
										/>
										{item.Category && (
											<Chip
												size="small"
												label={item.Category}
												sx={{
													position: "absolute",
													top: 8,
													right: 8,
													maxWidth: "70%",
													bgcolor: "background.paper",
													"& .MuiChip-label": {
														overflow: "hidden",
														textOverflow: "ellipsis",
													},
												}}
											/>
										)}
										{item.IsActive === false && (
											<Chip
												size="small"
												color="default"
												label={t("common:productCatalog.inactive")}
												sx={{ position: "absolute", top: 8, left: 8, bgcolor: "background.paper" }}
											/>
										)}
									</Box>
									<CardContent
										sx={{
											flex: "0 0 auto",
											width: "100%",
											boxSizing: "border-box",
											px: 1.25,
											pt: 1,
											pb: "12px !important",
											bgcolor: "background.paper",
											borderTop: 1,
											borderColor: "divider",
										}}
									>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											noWrap
											title={item.ItemCode}
										>
											{item.ItemCode || "—"}
										</Typography>
										<Typography
											variant="body2"
											fontWeight={700}
											color="text.primary"
											sx={{
												display: "-webkit-box",
												WebkitLineClamp: 2,
												WebkitBoxOrient: "vertical",
												overflow: "hidden",
												minHeight: "2.5em",
												lineHeight: 1.25,
												wordBreak: "break-word",
											}}
											title={item.ItemName}
										>
											{item.ItemName}
										</Typography>
										<Typography
											variant="body2"
											fontWeight={800}
											color="primary.main"
											sx={{ mt: 0.5, display: "block" }}
										>
											{Util.formatNumber(Number(item.SellingPrice ?? 0))}
										</Typography>
									</CardContent>
								</CardActionArea>
							</Card>
						))
					)}
				</Box>
			</Box>

			{filtered.length > 0 && (
				<Paper
					elevation={0}
					variant="outlined"
					sx={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						flexWrap: "wrap",
						gap: 1,
						px: 1.5,
						py: 1,
						bgcolor: "background.paper",
						zIndex: 1,
					}}
				>
					<Typography variant="caption" color="text.secondary" fontWeight={600}>
						{t("common:pos.layout.pageSummary", {
							from: Util.formatNumber(Math.min((pageSafe - 1) * PAGE_SIZE + 1, filtered.length)),
							to: Util.formatNumber(Math.min(pageSafe * PAGE_SIZE, filtered.length)),
							total: Util.formatNumber(filtered.length),
						})}
					</Typography>
					<Pagination
						size="small"
						color="primary"
						count={totalPages}
						page={pageSafe}
						onChange={(_, p) => setPage(p)}
						showFirstButton
						showLastButton
						disabled={loading}
					/>
				</Paper>
			)}

			{canCreate(access, "item") && (
			<Tooltip title={t("common:productCatalog.register")}>
				<Fab
					color="primary"
					aria-label={t("common:productCatalog.register")}
					onClick={openCreate}
					sx={{
						position: "absolute",
						// Clear the app bottom bar
						bottom: "calc(var(--footer-height, 50px) + 16px)",
						right: 24,
						zIndex: 20,
					}}
				>
					<AddIcon />
				</Fab>
			</Tooltip>
			)}

			<ItemDetailModal
				isOpen={detailOpen}
				toggle={closeDetail}
				item={selectedItem}
				createMode={createMode}
				onItemSaved={(updated, created) => {
					if (created) {
						if (updated.id > 0) {
							setItems((prev) => {
								const exists = prev.some((i) => i.id === updated.id);
								return exists
									? prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i))
									: [updated, ...prev];
							});
							setSelectedItem(updated);
							setCreateMode(false);
						} else {
							// Could not resolve id — full refresh
							void fetchCatalog();
						}
					} else {
						setItems((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
						setSelectedItem(updated);
					}
				}}
				onItemDeleted={(itemId) => {
					setItems((prev) => prev.filter((i) => i.id !== itemId));
					closeDetail();
				}}
				onImageUpdated={async (itemId) => {
					setImageBust((prev) => ({ ...prev, [itemId]: Date.now() }));
				}}
			/>
		</Box>
	);
};

export default ProductCatalog;
